import { useId, useRef } from 'react';
import { cn } from '@/shared/lib';
import { FieldLabel } from '@/shared/ui/field-label';
import { useAutoResize } from './hooks';
import type { TextareaProps } from './types';
import { controlStyles, fieldStyles } from './styles';

export type { TextareaProps, TextareaSize } from './types';

/**
 * Builds the `aria-describedby` value for a field.
 *
 * Hint and error can both be present, and both belong in the description — a
 * validation message the user cannot hear is not an accessible error.
 */
function describedBy(fieldId: string, hint?: string, error?: string): string | undefined {
  const ids = [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null].filter(
    (id): id is string => id !== null,
  );
  return ids.length > 0 ? ids.join(' ') : undefined;
}

/**
 * Multi-line text field with a bound label, optional hint, optional error, and
 * optional auto-resize.
 *
 * Identical accessibility contract to `Input` — the same label, `aria-invalid`
 * and `aria-describedby` wiring — because a field that behaves one way as a
 * single line and another as a paragraph is a field users learn twice.
 */
export function Textarea({
  label,
  hideLabel = false,
  hint,
  error,
  required = false,
  optional = false,
  size = 'md',
  autoResize = false,
  className,
  id,
  ...rest
}: TextareaProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useAutoResize(textareaRef, autoResize);

  return (
    <div className={fieldStyles.root}>
      <FieldLabel
        htmlFor={fieldId}
        required={required}
        optional={optional}
        visuallyHidden={hideLabel}
      >
        {label}
      </FieldLabel>
      <textarea
        {...rest}
        id={fieldId}
        ref={textareaRef}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fieldId, hint, error)}
        className={cn(
          controlStyles({ size, invalid: Boolean(error) }),
          autoResize && fieldStyles.autoResize,
          className,
        )}
      />
      {hint ? (
        <p id={`${fieldId}-hint`} className={fieldStyles.hint}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${fieldId}-error`} className={fieldStyles.error}>
          {error}
        </p>
      ) : null}
    </div>
  );
}